import React, { useEffect, useState, useCallback } from 'react';
import { ModalProps } from '@mui/base/Modal';
import { Article, ArticleTranslation } from '@/types/cms.types';
import { I18N_LANGUAGES } from '@/i18n';
import {
  useTranslateArticleMutation,
  useUploadImageMutation,
} from '@/services/ArticleService';
import { useSnackbar } from 'notistack';
import { Editor } from '@tinymce/tinymce-react';
import { usePermissions } from '@/hooks/usePermissions';
import { tinymceInit, TINYMCE_SCRIPT_SRC } from '@/constant';
import { Drawer } from '@/components/drawer/Drawer';

/* ---------- TinyMCE types ---------- */
interface BlobInfo {
  blob: () => Blob;
  filename: () => string;
  base64: () => string;
}

/* ---------- Props ---------- */
export interface ArticlePreviewModalProps extends Omit<ModalProps, 'children'> {
  formData: Partial<Article>;
  source_language?: string;
  /** Pre-existing translations from the API (edit mode). When provided the
   *  modal skips the auto-fetch and uses these directly. */
  initialTranslations?: Record<string, ArticleTranslation>;
  onTranslationsGenerated?: (
    translations: Record<string, ArticleTranslation>
  ) => void;
  /** Called when the user edits the source-language card inside the modal so
   *  the parent form can stay in sync. */
  onSourceFormDataChange?: (
    fields: Pick<Article, 'title' | 'summary' | 'body'>
  ) => void;
}

export interface LanguageEditorProps {
  language: {
    code: string;
    label: string;
    flag: string;
  };
  translation: ArticleTranslation;
  isLoading: boolean;
  isEditMode: boolean;
  onToggleEdit: () => void;
  onUpdate: (field: keyof ArticleTranslation, value: string | string[]) => void;
  uploadImage: (formData: FormData) => Promise<{ url: string }>;
}

/* ---------- Language Editor ---------- */
export const LanguageEditor: React.FC<LanguageEditorProps> = ({
  language,
  translation,
  isLoading,
  isEditMode,
  onToggleEdit,
  onUpdate,
  uploadImage,
}) => {
  return (
    <div className="mb-5 rounded-xl border border-gray-300 overflow-hidden shadow-sm">
      {/* Header */}
      <div className="px-5 py-3 flex items-center justify-between bg-gray-100 border-b border-gray-300">
        <div className="flex items-center">
          <img
            src={language.flag}
            alt={language.label}
            className="w-6 h-6 mr-3 rounded-sm shadow-sm"
          />
          <h2 className="text-base font-medium text-gray-900 flex items-center">
            {language.label}
            {isLoading && (
              <span className="ml-2 w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            )}
          </h2>
        </div>

        <button
          onClick={onToggleEdit}
          disabled={isLoading}
          className={`btn btn-sm ${isEditMode ? 'btn-light' : 'btn-primary'}`}
        >
          {isEditMode ? 'View Preview' : 'Edit'}
        </button>
      </div>

      {/* Body */}
      <div className="p-5 space-y-4">
        {/* Title */}
        <div>
          <span className="form-label text-sm font-medium text-gray-700">
            Title
          </span>

          {isEditMode ? (
            <input
              type="text"
              value={translation.title || ''}
              onChange={(e) => onUpdate('title', e.target.value)}
              placeholder="Article title"
              className="input input-bordered w-full"
            />
          ) : (
            <div className="min-h-[42px] p-3 rounded border bg-gray-100 text-gray-900 border-gray-300">
              {translation.title || (
                <span className="text-gray-700">
                  No title
                </span>
              )}
            </div>
          )}
        </div>

        {/* Summary */}
        <div>
          <span className="form-label text-sm font-medium text-gray-700">
            Summary
          </span>

          {isEditMode ? (
            <textarea
              value={translation.summary || ''}
              onChange={(e) => onUpdate('summary', e.target.value)}
              placeholder="Article summary"
              className="textarea textarea-bordered w-full h-24"
            />
          ) : (
            <div className="min-h-[96px] whitespace-pre-wrap p-3 rounded border bg-gray-100 text-gray-900 border-gray-300">
              {translation.summary || (
                <span className="text-gray-700">
                  No summary
                </span>
              )}
            </div>
          )}
        </div>

        {/* Body */}
        <div>
          <span className="form-label text-sm font-medium text-gray-700">
            Body Content
          </span>

          {isEditMode ? (
            <div className="rounded border border-gray-300 overflow-hidden">
              <Editor
                tinymceScriptSrc={TINYMCE_SCRIPT_SRC}
                value={translation.body || ''}
                init={{
                  ...tinymceInit,
                  height: 600,
                  images_file_types: 'jpg,jpeg,png',
                  toolbar: tinymceInit.toolbar.concat('image'),
                  images_upload_handler: (blobInfo: BlobInfo) =>
                    new Promise((resolve, reject) => {
                      const file = blobInfo.blob();
                      if (
                        !['image/jpeg', 'image/jpg', 'image/png'].includes(
                          file.type
                        )
                      ) {
                        reject('Only JPG, JPEG and PNG images are allowed');
                        return;
                      }
                      const formData = new FormData();
                      formData.append('image', file, blobInfo.filename());
                      uploadImage(formData)
                        .then((res) => resolve(res.url))
                        .catch(() => reject('Image upload failed'));
                    }),
                }}
                onEditorChange={(content: string) => onUpdate('body', content)}
              />
            </div>
          ) : (
            <div
              className="min-h-[300px] p-3 rounded border border-gray-300 bg-gray-100 prose prose-lg max-w-none text-gray-800 leading-relaxed
                  [--tw-prose-bullets:var(--tw-gray-600)] [--tw-prose-counters:var(--tw-gray-700)]
                  [--tw-prose-hr:var(--tw-gray-300)] [--tw-prose-quote-borders:var(--tw-gray-300)]
                  [--tw-prose-th-borders:var(--tw-gray-400)] [--tw-prose-td-borders:var(--tw-gray-300)]
                  [--tw-prose-captions:var(--tw-gray-700)]
                  prose-headings:text-gray-900 prose-strong:text-gray-900 prose-headings:font-bold
                  prose-a:text-primary-active hover:prose-a:text-primary-active contrast:prose-a:text-blue-300 contrast:hover:prose-a:text-blue-200 prose-blockquote:text-gray-700
                  prose-code:text-gray-900 prose-ol:text-gray-800 prose-ul:text-gray-800
                  prose-pre:bg-gray-200 prose-pre:text-gray-800 prose-pre:border prose-pre:border-gray-300 prose-pre:rounded prose-pre:p-4
                  prose-p:mb-6 prose-li:mb-2
                  prose-img:rounded-xl prose-img:shadow-lg"
              dangerouslySetInnerHTML={{
                __html: translation.body || '<p>No content</p>',
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

/* ---------- Modal ---------- */
const ArticlePreviewModal: React.FC<ArticlePreviewModalProps> = ({
  formData,
  source_language,
  open,
  onClose,
  className,
  initialTranslations,
  onTranslationsGenerated,
  onSourceFormDataChange,
}) => {
  const { hasPermission } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();

  const [translations, setTranslations] = useState<
    Record<string, ArticleTranslation>
  >(() => initialTranslations ?? formData.translations ?? {});
  const [editModes, setEditModes] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(false);

  // Sync whenever the parent updates pre-existing translations (e.g. on re-open or edit).
  useEffect(() => {
    if (initialTranslations && Object.keys(initialTranslations).length > 0) {
      setTranslations(initialTranslations);
    }
  }, [initialTranslations]);

  const [translateArticle] = useTranslateArticleMutation();
  const [uploadImage] = useUploadImageMutation();

  const toggleEditMode = (code: string) =>
    setEditModes((p) => ({ ...p, [code]: !p[code] }));

  const handleUpdate = (
    code: string,
    field: keyof ArticleTranslation,
    value: string | string[]
  ) => {
    // If the user edits the source-language card, propagate changes back to
    // the parent form so the two stay in sync.
    if (code === (source_language || 'en') && onSourceFormDataChange) {
      if (field === 'title' || field === 'summary' || field === 'body') {
        onSourceFormDataChange({
          title: field === 'title' ? (value as string) : (formData.title ?? ''),
          summary:
            field === 'summary' ? (value as string) : (formData.summary ?? ''),
          body: field === 'body' ? (value as string) : (formData.body ?? ''),
        });
      }
      // Don't write source into translations map — it lives in formData
      return;
    }

    setTranslations((p) => ({
      ...p,
      [code]: { ...p[code], [field]: value },
    }));
  };

  const fetchTranslations = useCallback(async () => {
    if (!formData.title && !formData.summary && !formData.body) {
      enqueueSnackbar('Please provide title, summary, or body to translate', {
        variant: 'warning',
      });
      return;
    }

    setIsLoading(true);
    try {
      const res = await translateArticle({
        title: formData.title,
        summary: formData.summary,
        body: formData.body,
        source_language: source_language || 'en',
      }).unwrap();

      setTranslations(res.translations || {});
      onTranslationsGenerated?.(res.translations || {});
      if (res.warning_message) {
        enqueueSnackbar(res.warning_message, {
          variant: 'warning',
        });
      } else {
        enqueueSnackbar('Translations generated successfully', {
          variant: 'success',
        });
      }
    } catch {
      enqueueSnackbar('Translation failed', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [
    formData,
    source_language,
    translateArticle,
    enqueueSnackbar,
    onTranslationsGenerated,
  ]);

  useEffect(() => {
    // Only auto-fetch when there are NO translations at all (i.e. create mode
    // with no prior data). If any translations are already available
    // (initialTranslations seeded from edit mode, or previously generated)
    // we skip the API call — the user must click "Regenerate" explicitly.
    if (open && Object.keys(translations).length === 0) {
      fetchTranslations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    onTranslationsGenerated?.(translations);
    setEditModes({});
    onClose?.({}, 'backdropClick');
  };

  // Show all languages (including source) in the preview list, same as Harbor.
  const targetLanguages = I18N_LANGUAGES;

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      className={className}
      anchor="right"
      slotProps={{
        paper: {
          sx: {
            width: { xs: '100vw', sm: '756px' },
            maxWidth: '100vw',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
          role: 'dialog',
          'aria-modal': true,
          'aria-labelledby': 'article-translation-preview-title',
        },
      }}
    >
      <section
        className="flex flex-col h-full min-h-0 bg-[var(--tw-modal-background-color)]"
        aria-label="Article translation preview"
        aria-labelledby="article-translation-preview-title"
      >
        <div className="modal-header shrink-0">
          <h3 id="article-translation-preview-title" className="modal-title">
            Article Translation Preview
          </h3>
          <button
            className="btn btn-sm btn-icon btn-light"
            onClick={handleClose}
          >
            ×
          </button>
        </div>

        <div className="modal-body flex-1 overflow-y-auto min-h-0">
          {targetLanguages.map((lang) => {
            const isSource = lang.code === (source_language || 'en');

            // For the source language, derive the translation from formData
            // so the card always reflects the live values in the editor.
            const translationData: ArticleTranslation = isSource
              ? {
                  title: formData.title ?? '',
                  summary: formData.summary ?? '',
                  body: formData.body ?? '',
                  tags: formData.tags ?? [],
                }
              : (translations[lang.code] ?? {
                  title: '',
                  summary: '',
                  body: '',
                  tags: [],
                });

            return (
              <div key={lang.code}>
                {isSource && (
                  <p className="text-xs font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300 contrast:text-blue-300 mb-1 px-1">
                    Source Language
                  </p>
                )}
                <LanguageEditor
                  language={lang}
                  translation={translationData}
                  isLoading={isLoading}
                  isEditMode={editModes[lang.code] || false}
                  onToggleEdit={() => toggleEditMode(lang.code)}
                  onUpdate={(f, v) => handleUpdate(lang.code, f, v)}
                  uploadImage={(fd) => uploadImage(fd).unwrap()}
                />
              </div>
            );
          })}
        </div>

        <div className="modal-footer shrink-0">
          <button
            type="button"
            className="btn btn-light"
            disabled={
              isLoading || !hasPermission('article_management_translate')
            }
            onClick={fetchTranslations}
          >
            Regenerate Translations
          </button>
          <button
            className="btn btn-primary"
            disabled={!hasPermission('article_management_translate')}
            onClick={handleClose}
          >
            Save & Close
          </button>
        </div>
      </section>
    </Drawer>
  );
};

export default ArticlePreviewModal;
